import React, { memo, useLayoutEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { FONTS } from '../../config/app-config';
import useArticleWindow from '../../hooks/useArticleWindow';
import ArticleOperations from './ArticleOperations';
import ArticleScrollAnchor from './ArticleScrollAnchor';

/** Prepare generated HTML once before measuring its final article height. */
export function prepareArticle(section) {
    section.querySelectorAll('table').forEach(table => {
        table.style.minWidth = '700px';
        table.style.width = 'auto';
        if (table.parentElement.classList.contains('scroll-wrapper')) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'scroll-wrapper';
        Object.assign(wrapper.style, { overflowX: 'auto', width: '100%', maxWidth: '100%', boxSizing: 'border-box' });
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
    });
    section.querySelectorAll('span[data-glossaryitemid]').forEach(span => {
        const link = document.createElement('a');
        link.href = '#';
        link.className = 'glossary-link';
        link.dataset.glossaryitemid = span.dataset.glossaryitemid;
        link.textContent = span.textContent;
        span.replaceWith(link);
    });
    section.querySelectorAll('span.popuponclicktemplate').forEach(span => {
        const source = span.querySelector('a[data-itemid]');
        if (!source) return;
        const link = document.createElement('a');
        link.href = '#';
        link.className = 'embedded-help-link';
        link.dataset.embeddedarticleid = source.dataset.itemid;
        link.textContent = source.textContent.trim();
        span.replaceWith(link);
    });
}

const ArticleSection = memo(function ArticleSection({
    index,
    virtualIndex,
    article,
    body,
    selectedRelease,
    offlineMode,
    measureElement,
}) {
    const ref = useRef(null);
    useLayoutEffect(() => {
        prepareArticle(ref.current);
        measureElement(ref.current);
    }, [body, measureElement]);
    return (
        <Box
            ref={ref}
            data-index={index}
            data-virtual-index={virtualIndex}
            className="article-section"
            sx={{ p: 3, borderBottom: 1, borderColor: 'divider', hyphens: 'auto' }}
        >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h5" fontFamily={FONTS.heading}>{article?.title || 'Loading…'}</Typography>
                {!offlineMode && (
                    <ArticleOperations
                        articleId={article?._id}
                        selectedRelease={selectedRelease}
                        initiallyFavourite={article?.isFavourite}
                        title={article?.title}
                    />
                )}
            </Box>
            <Box sx={{ maxWidth: '1200px', minWidth: 0 }}>{body}</Box>
        </Box>
    );
});

export default memo(function VirtualArticleList({
    articles,
    flatToc,
    containerRef,
    contentRef,
    scrollAdjustmentRef,
    selectedRelease,
    offlineMode,
}) {
    const articleWindow = useArticleWindow({ articles, flatToc, containerRef, contentRef, scrollAdjustmentRef });

    // Spacers retain scroll geometry while only the visible articles are mounted.
    return (
        <ArticleScrollAnchor
            indices={articleWindow.indices}
            rangeStart={articleWindow.items[0]?.key}
            rangeEnd={articleWindow.items[articleWindow.items.length - 1]?.key}
            containerRef={containerRef}
            onRestore={articleWindow.restoreAnchor}
        >
            <div aria-hidden="true" data-reader-spacer="top" style={{ height: articleWindow.topSpace }} />
            {articleWindow.items.map(item => {
                const index = articleWindow.indices[item.index];
                return (
                    <ArticleSection
                        key={item.key}
                        index={index}
                        virtualIndex={item.index}
                        article={flatToc[index]}
                        body={articles[index]}
                        selectedRelease={selectedRelease}
                        offlineMode={offlineMode}
                        measureElement={articleWindow.measureElement}
                    />
                );
            })}
            <div aria-hidden="true" data-reader-spacer="bottom" style={{ height: articleWindow.bottomSpace }} />
        </ArticleScrollAnchor>
    );
});
